import type { Tile } from '@rummikub/engine';
import { validateMeld } from '@rummikub/engine';

const COLOR_ORDER: Record<string, number> = { black: 0, red: 1, blue: 2, orange: 3 };

/** 依數字優先排序（789 順子順） */
export function sortByNumber(rack: readonly Tile[]): Tile[] {
  const arr = rack.slice();
  arr.sort((a, b) => {
    if (a.isJoker !== b.isJoker) return a.isJoker ? 1 : -1; // joker 放最後
    const na = a.number ?? 0;
    const nb = b.number ?? 0;
    if (na !== nb) return na - nb;
    return (COLOR_ORDER[a.color ?? ''] ?? 99) - (COLOR_ORDER[b.color ?? ''] ?? 99);
  });
  return arr;
}

/** 依顏色優先排序（777 群組順） */
export function sortByColor(rack: readonly Tile[]): Tile[] {
  const arr = rack.slice();
  arr.sort((a, b) => {
    if (a.isJoker !== b.isJoker) return a.isJoker ? 1 : -1;
    const ca = COLOR_ORDER[a.color ?? ''] ?? 99;
    const cb = COLOR_ORDER[b.color ?? ''] ?? 99;
    if (ca !== cb) return ca - cb;
    return (a.number ?? 0) - (b.number ?? 0);
  });
  return arr;
}

/**
 * 在 meld 中找最適合的插入 index，使得插入後仍合法 / 至少有序。
 * 群組：固定顏色順序；順子：依 number 升序插入。
 */
/**
 * 從手牌中找出所有可組成合法 meld 的組合。
 * 789 模式優先順子（同色連號），777 模式優先群組（同數字不同色）。
 * 回傳互不重疊的 meld 陣列。
 */
export function findRackMelds(rack: readonly Tile[], mode: '789' | '777'): Tile[][] {
  const candidates: Tile[][] = [];

  if (mode === '789') {
    const byColor = new Map<string, Tile[]>();
    for (const t of rack) {
      if (t.isJoker || !t.color || t.number == null) continue;
      const arr = byColor.get(t.color) ?? [];
      arr.push(t);
      byColor.set(t.color, arr);
    }
    for (const [, tiles] of byColor) {
      tiles.sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
      const uniq: Tile[] = [];
      for (const t of tiles) {
        const last = uniq[uniq.length - 1];
        if (!last || last.number !== t.number) uniq.push(t);
      }
      let run: Tile[] = [];
      for (let i = 0; i < uniq.length; i++) {
        const t = uniq[i]!;
        const last = run[run.length - 1];
        if (!last || (last.number != null && t.number === last.number + 1)) {
          run.push(t);
        } else {
          if (run.length >= 3) candidates.push(run);
          run = [t];
        }
      }
      if (run.length >= 3) candidates.push(run);
    }
  } else {
    const byNumber = new Map<number, Tile[]>();
    for (const t of rack) {
      if (t.isJoker || t.number == null || !t.color) continue;
      const arr = byNumber.get(t.number) ?? [];
      arr.push(t);
      byNumber.set(t.number, arr);
    }
    for (const [, tiles] of byNumber) {
      const uniq = new Map<string, Tile>();
      for (const t of tiles) if (t.color && !uniq.has(t.color)) uniq.set(t.color, t);
      const group = [...uniq.values()];
      if (group.length >= 3) candidates.push(group);
    }
  }

  // Greedy 挑選互不重疊的 meld（依分數高到低）
  candidates.sort((a, b) => meldScore(b) - meldScore(a));
  const used = new Set<string>();
  const picked: Tile[][] = [];
  for (const m of candidates) {
    if (m.some((t) => used.has(t.id))) continue;
    if (!validateMeld(m).valid) continue;
    picked.push(m);
    for (const t of m) used.add(t.id);
  }
  return picked;
}

function meldScore(meld: Tile[]): number {
  return meld.reduce((s, t) => s + (t.number ?? 0), 0);
}

/** 將找到的 melds 排在前面（meld 之間留 1 格空隙），其餘散牌排在後面。回傳每張牌對應的 slot index。 */
export function arrangeRackWithGaps(
  rack: readonly Tile[],
  mode: '789' | '777',
): { slotMap: Map<string, number>; melds: Tile[][]; ordered: Tile[] } {
  const melds = findRackMelds(rack, mode);
  const inMeld = new Set<string>();
  const slotMap = new Map<string, number>();
  let slot = 0;
  for (let i = 0; i < melds.length; i++) {
    const m = melds[i]!;
    for (const t of m) {
      slotMap.set(t.id, slot++);
      inMeld.add(t.id);
    }
    if (i < melds.length - 1 || true) slot++; // meld 後留 1 格空隙（最後一組也要留，與散牌分開）
  }
  const rest = rack.filter((t) => !inMeld.has(t.id));
  const sortedRest = mode === '789' ? sortByColor(rest) : sortByNumber(rest);
  for (const t of sortedRest) slotMap.set(t.id, slot++);
  const ordered = [...melds.flat(), ...sortedRest];
  return { slotMap, melds, ordered };
}

export function suggestInsertIndex(meld: readonly Tile[], tile: Tile): number {
  if (meld.length === 0) return 0;
  // 若 meld 像順子（同色），依數字排序
  const sameColor = meld.every((t) => t.isJoker || t.color === meld.find((x) => !x.isJoker)?.color);
  if (sameColor && !tile.isJoker && tile.number != null) {
    for (let i = 0; i < meld.length; i++) {
      const t = meld[i]!;
      if (!t.isJoker && t.number != null && t.number > tile.number) return i;
    }
    return meld.length;
  }
  // 群組：依顏色槽位插入
  if (!tile.isJoker && tile.color) {
    const co = COLOR_ORDER[tile.color] ?? 99;
    for (let i = 0; i < meld.length; i++) {
      const t = meld[i]!;
      if (!t.isJoker && t.color && (COLOR_ORDER[t.color] ?? 99) > co) return i;
    }
    return meld.length;
  }
  return meld.length;
}
